import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { chatKeys } from '~/lib/query-keys';
import {
  useBackgroundChatForSession,
  type BackgroundChatStatus,
} from '~/hooks/queries/use-background-chats';

// FRONT-2001: refresh the chat when a GPT-Live-1 call's handed-off job
// delivers its result from another pod.
//
// A handed-off voice job settles on whichever pod runs it and writes its
// answer straight into `chat_messages` (`deliverLateResult`,
// `src/voice/live-jobs.ts`). The `background-chat-changed` event that
// normally repaints the chat travels on that pod's own event bus, so a tab
// whose live-update stream is held by a different pod never hears it.
//
// The per-session background-chat query is what this hook watches, but that
// query only refetches on that same bus event or while it already holds a
// `running` run. So `use-voice-call.ts` re-reads it once, shortly after a
// delegation comes back handed off (the run row exists by then), and from
// there the query's own 2s poll carries it to the settle.
//
// Voice-job runs only (`vlj_` ids). An ordinary typed turn passes through
// `running` → `completed` on the same query, and its history already has its
// writers (`local-history-writer.ts`, FRONT-1717) — refreshing on it here
// would put a second writer back on every turn.
//
// Invalidation only — never a cache write.

export const VOICE_JOB_RUN_PREFIX = 'vlj_';

export interface ObservedRun {
  runId: string;
  status: BackgroundChatStatus;
}

/**
 * Whether the latest background run for a chat just reported a voice job's
 * result landing. `prev` is the last run observed for THIS session:
 * `undefined` when nothing has been observed yet (mount, a switched chat —
 * never a reason to refetch), `null` when the chat had no run.
 *
 * True only when `next` is a SETTLED voice-job run that is news: it was seen
 * running before, or it is a different run than the one last seen (a job
 * that started and settled between two reads).
 */
export function shouldRefetchHistoryOnSettle(
  prev: ObservedRun | null | undefined,
  next: ObservedRun | null | undefined,
): boolean {
  if (prev === undefined || !next) return false;
  if (!next.runId.startsWith(VOICE_JOB_RUN_PREFIX) || next.status === 'running') return false;
  return prev === null || prev.runId !== next.runId || prev.status === 'running';
}

/**
 * Mount alongside `useVoiceCall` wherever a chat surface can receive a Live
 * call's late result: `chat-input.tsx` (desktop) and `pwa-shell.tsx` (the
 * phone surface). Pass the call's own resolved `sessionId` (returned by
 * `useVoiceCall`) — a host's raw session prop is undefined on the main chat.
 */
export function useRefreshHistoryWhenBackgroundSettles(sessionId: string | undefined): void {
  const queryClient = useQueryClient();
  const { data: run } = useBackgroundChatForSession(sessionId);
  const loaded = run !== undefined;
  const runId = run?.runId;
  const status = run?.status;
  const prevRef = useRef<{ sessionId: string | undefined; observed: ObservedRun | null | undefined }>({
    sessionId: undefined,
    observed: undefined,
  });

  useEffect(() => {
    if (!sessionId || !loaded) return;
    const next: ObservedRun | null = runId && status ? { runId, status } : null;
    const prev = prevRef.current.sessionId === sessionId ? prevRef.current.observed : undefined;
    if (shouldRefetchHistoryOnSettle(prev, next)) {
      // The history PREFIX: the job writes to its own branch, which may not
      // be the branch this tab shows.
      queryClient.invalidateQueries({ queryKey: [...chatKeys.all, 'history', sessionId] });
    }
    prevRef.current = { sessionId, observed: next };
  }, [sessionId, loaded, runId, status, queryClient]);
}
